import type { FC, PropsWithChildren, ReactNode } from "react";
import { Dialog, Heading, type DialogProps } from "react-aria-components";
import { ModalCloseButton } from "../Modal/modalCloseButton.js";

//
// Main dialog component.
//

interface SlideoverDialogProps {
  label: string;
  children: DialogProps["children"];
}

export const SlideoverDialog: FC<SlideoverDialogProps> = (props) => {
  const { label, children } = props;
  return (
    <Dialog aria-label={label} className="isolate flex h-full flex-col">
      {children}
    </Dialog>
  );
};

//
// Dialog header component.
//

interface SlideoverDialogHeaderProps extends PropsWithChildren {
  isLoading?: boolean;
  right?: ReactNode;
}

export const SlideoverDialogHeader: FC<SlideoverDialogHeaderProps> = (
  props,
) => {
  const { isLoading, children } = props;

  const contentRender = (() => {
    if (isLoading) {
      return (
        <div className="my-2 h-9 max-w-60 flex-1 self-center py-1">
          <div className="h-full w-full animate-pulse rounded-full bg-gray-300" />
          <Heading slot="title" className="invisible">
            Loading...
          </Heading>
        </div>
      );
    }

    return <div className="flex min-w-0 items-center gap-8">{children}</div>;
  })();

  return (
    <div className="flex h-[60px] border-b border-border-light bg-background px-4">
      <div className="flex min-w-0 flex-1 items-stretch justify-between gap-3">
        {contentRender}
        <div className="flex h-9 items-center gap-3 self-center">
          {props.right}
          <ModalCloseButton />
        </div>
      </div>
    </div>
  );
};

//
// Dialog title component.
//

export const SlideoverDialogTitle: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  return (
    <Heading
      slot="title"
      className="truncate text-base leading-6 font-semibold text-foreground"
    >
      {children}
    </Heading>
  );
};

//
// Dialog title header component.
//

export const SlideoverDialogTitleHeader: FC<SlideoverDialogHeaderProps> = (
  props,
) => {
  const { children } = props;
  return (
    <SlideoverDialogHeader {...props}>
      <SlideoverDialogTitle>{children}</SlideoverDialogTitle>
    </SlideoverDialogHeader>
  );
};

//
// Dialog content component.
//

export const SlideoverDialogContent: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  return <div className="flex flex-1 flex-col overflow-y-auto">{children}</div>;
};

export const SlideoverDialogContentGray: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  return (
    <div className="flex flex-1 flex-col overflow-y-auto bg-background">
      {children}
    </div>
  );
};

//
// Dialog buttons component.
//

export const SlideoverDialogButtons: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  return (
    <div className="flex h-[60px] justify-end space-x-3 border-t border-border-light bg-white px-6 py-3">
      {children}
    </div>
  );
};
